import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Shield,
  Search,
  RefreshCw,
  Loader,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  User,
  MessageSquare,
  Calendar,
  Eye,
  Check,
  X
} from 'lucide-react';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useTheme } from '../../../contexts/ThemeContext';

const RegistrationManagement = ({ onUpdate }) => {
  const { getProtectedTenantInfo } = useTenantProtection();
  const { isDark } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  const getCurrentTenant = () => {
    return getProtectedTenantInfo();
  };

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/registrations/${tenant.code}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRegistrations(data.data || []);
      } else {
        throw new Error('Failed to load registrations');
      }
    } catch (error) {
      console.error('Failed to load Line registrations:', error);
      toast.error('Failed to load Line registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRegistrations();
  }, []);

  const handleRefresh = () => {
    loadRegistrations();
  };

  const handleApprove = async (registrationId) => {
    if (!window.confirm('Are you sure you want to approve this registration?')) {
      return;
    }

    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/registrations/${tenant.code}/${registrationId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('Registration approved successfully');
        loadRegistrations();
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Failed to approve registration');
      }
    } catch (error) {
      console.error('Failed to approve registration:', error);
      toast.error('Failed to approve registration');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (registrationId) => {
    if (!window.confirm('Are you sure you want to reject this registration?')) {
      return;
    }

    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/registrations/${tenant.code}/${registrationId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('Registration rejected successfully');
        loadRegistrations();
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Failed to reject registration');
      }
    } catch (error) {
      console.error('Failed to reject registration:', error);
      toast.error('Failed to reject registration');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemove = async (registrationId) => {
    if (!window.confirm('Are you sure you want to remove this registration record?')) {
      return;
    }

    try {
      setProcessing(true);
      const tenant = getCurrentTenant();
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${backendUrl}/api/line-management/registrations/${tenant.code}/${registrationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('Registration removed successfully');
        loadRegistrations();
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Failed to remove registration');
      }
    } catch (error) {
      console.error('Failed to remove registration:', error);
      toast.error('Failed to remove registration');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDetails = (registration) => {
    setSelectedRegistration(registration);
    setShowDetailModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'verified':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      case 'verified':
        return <Shield className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getStepProgress = (step) => {
    const steps = ['initiated', 'verification_sent', 'verified', 'approved'];
    const currentIndex = steps.indexOf(step);
    return ((currentIndex + 1) / steps.length) * 100;
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = 
      reg.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.lineUserId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.groupName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || reg.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div className="flex space-x-2">
          {['all', 'pending', 'verified', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-blue-600 text-white'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {status !== 'all' && (
                <span className="ml-2">
                  ({registrations.filter(r => r.status === status).length})
                </span>
              )}
            </button>
          ))}
        </div>

        <button
          onClick={handleRefresh}
          disabled={loading}
          className={`flex items-center space-x-2 px-4 py-2 border rounded-md ${
            isDark 
              ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          } disabled:opacity-50`}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search registrations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            isDark 
              ? 'border-gray-600 bg-gray-700 text-white' 
              : 'border-gray-300 bg-white text-gray-900'
          }`}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    User/Group
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Type
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Progress
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Date
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredRegistrations.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <Shield className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                      <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        No registrations found
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredRegistrations.map((registration) => (
                    <tr key={registration.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            {registration.type === 'group' ? (
                              <MessageSquare className="h-5 w-5 text-gray-500" />
                            ) : (
                              <User className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {registration.displayName || registration.groupName || 'Unknown'}
                            </div>
                            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {registration.email || registration.lineUserId || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          registration.type === 'group' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {registration.type === 'group' ? 'Group' : 'User'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex items-center space-x-1 text-xs leading-5 font-semibold rounded-full ${getStatusColor(registration.status)}`}>
                          {getStatusIcon(registration.status)}
                          <span>{registration.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${getStepProgress(registration.registrationStep || 'initiated')}%` }}
                          />
                        </div>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {registration.registrationStep || 'initiated'}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm flex items-center space-x-1 ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          <Calendar className="h-3 w-3" />
                          <span>{registration.createdAt ? new Date(registration.createdAt).toLocaleDateString() : 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewDetails(registration)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {registration.status === 'pending' || registration.status === 'verified' ? (
                            <>
                              <button
                                onClick={() => handleApprove(registration.id)}
                                disabled={processing}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                title="Approve"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleReject(registration.id)}
                                disabled={processing}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                title="Reject"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleRemove(registration.id)}
                              disabled={processing}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Remove"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl max-w-2xl w-full mx-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Registration Details
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className={`h-20 w-20 rounded-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    {selectedRegistration.type === 'group' ? (
                      <MessageSquare className="h-10 w-10 text-gray-500" />
                    ) : (
                      <User className="h-10 w-10 text-gray-500" />
                    )}
                  </div>
                  <div>
                    <h4 className={`text-xl font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {selectedRegistration.displayName || selectedRegistration.groupName || 'Unknown'}
                    </h4>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {selectedRegistration.type === 'group' ? 'Group Registration' : 'User Registration'}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Line User/Group ID</p>
                    <p className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {selectedRegistration.lineUserId || selectedRegistration.groupId || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Email</p>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {selectedRegistration.email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</p>
                    <span className={`px-2 inline-flex items-center space-x-1 text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedRegistration.status)}`}>
                      {getStatusIcon(selectedRegistration.status)}
                      <span>{selectedRegistration.status}</span>
                    </span>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Registration Step</p>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {selectedRegistration.registrationStep || 'initiated'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Created</p>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {selectedRegistration.createdAt ? new Date(selectedRegistration.createdAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Updated</p>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {selectedRegistration.updatedAt ? new Date(selectedRegistration.updatedAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>

                {selectedRegistration.verificationToken && (
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Verification Token</p>
                    <p className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {selectedRegistration.verificationToken}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-between">
                <div className="space-x-2">
                  {(selectedRegistration.status === 'pending' || selectedRegistration.status === 'verified') && (
                    <>
                      <button
                        onClick={() => {
                          handleApprove(selectedRegistration.id);
                          setShowDetailModal(false);
                        }}
                        disabled={processing}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          handleReject(selectedRegistration.id);
                          setShowDetailModal(false);
                        }}
                        disabled={processing}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationManagement;

